import useWidgetViewManager from '@/composables/widget-view-manager'
import { useWorkflow } from '@/composables/workflow'
import { promiseTimeout, until, useEventBus } from '@vueuse/core'
import useWebsocket, { WebSocketConnectionError } from '@/api/websocket'
import { updateSentryContext } from '@/lib/sentry'
import { setupTracker } from '@/composables/tracker'
import { updateOrchestratorApi } from '@/api/orchestrator'
import { updateAppsApi } from '@/api/apps'
import { useI18n } from '../i18n'
import useEmbed from '@/embed/code'
import { computed, ref } from 'vue'
import type { Message, TokenDecoded, ValidateTokenResponseError, WidgetOptions } from '@/types'
import useWidgetOptions from '@/composables/widget-options'
import { useCustomCss } from '../custom-css'
import { useRemoteWidgetOptions } from '../remote-widget-options/use-remote-widget-options'
import type { AxiosError } from 'axios'
import { logger } from '@sentry/vue'
import { path, pick } from 'ramda'

const INITIALIZATION_NO_CONTEXT = 'initialization-no-context'
const SESSION_EXPIRED = 'SESSION_EXPIRED'

const preInitialized = ref(false)
const preInitializing = ref(false)
const initialized = ref(false)
const widgetOpened = ref(false)
const initializing = ref(false)
let websocketReadyPromise: Promise<void> | undefined

function useWidgetHooks() {
    const preInitialize = async (token: string, options?: Partial<WidgetOptions>, clientDomain?: string) => {
        logger.trace(`Preinitialize called`, {
            token: !!token,
            options: options,
        })

        const workspace = useWorkflow()
        const { setOptions } = useWidgetOptions()
        const { setupCustomCss } = useCustomCss()
        const { initialize: initializeWidgetViewManager } = useWidgetViewManager()
        const { t } = useI18n()

        if (preInitializing.value) {
            return
        }

        try {
            if (preInitialized.value) {
                throw new Error('already preinitialized')
            }
            if (!token) {
                throw INITIALIZATION_NO_CONTEXT
            }

            preInitializing.value = true

            let decodedToken = {} as TokenDecoded

            try {
                decodedToken = workspace.setToken(token)
            } catch (error) {
                useWidgetViewManager().showError({
                    title: t('views.widget.invalid_token.title'),
                    description: t('views.widget.invalid_token.description'),
                    error,
                    retry: false,
                    close: true,
                    svgIcon: 'warning.svg',
                })

                useEmbed().emitErrorEvent({
                    code: 'fail',
                    message: 'token decode error',
                })

                return
            }

            logger.trace(`T0ken decoded`)

            if (options) {
                setOptions(options)
            }

            const mode = workspace.mode.value

            const { partyId, workflowDefinitionId, workspaceId, version, workflowId } = decodedToken

            updateSentryContext({
                partyId,
                workspaceId,
                workflowDefinitionId,
                version,
                mode,
                workflowId,
            })

            logger.info('Sentry Context updated', {
                partyId,
                workspaceId,
                workflowDefinitionId,
                version,
                mode,
                workflowId,
            })

            setupTracker(partyId, workflowDefinitionId, mode, workspaceId, clientDomain)

            updateOrchestratorApi(mode)
            updateAppsApi(mode)

            logger.trace(`Api clients setup`)

            if (decodedToken.version >= 3) {
                const { initialize: initializeWebsocket } = useWebsocket()
                websocketReadyPromise = initializeWebsocket(token, `${workflowId}`, mode)
            }

            const mergedOptions: Partial<WidgetOptions> = {
                ...options || {},
            }

            try {
                const remoteOptions = await useRemoteWidgetOptions().getOptions()

                if (remoteOptions) {
                    const { widgetOptions, cssVariables } = remoteOptions

                    const usedRemoteOptions = options?.tlHosted || import.meta.env.DEV
                        ? widgetOptions?.hosted
                        : widgetOptions?.embedded

                    Object.assign(mergedOptions, {
                        ...usedRemoteOptions,
                        cssVariables,
                    })
                }

                logger.trace(remoteOptions
                    ? `Widget options downloaded and merged`
                    : `Received empty widget options from API`, {
                    widgetOptions: mergedOptions,
                })
            } catch (e: unknown) {
                logger.trace('Get remote widget options error', {
                    error: e,
                })
                console.error(e) // eslint-disable-line no-console
            }

            setOptions(mergedOptions)
            useEmbed().setWrapperOptions(mergedOptions as WidgetOptions)

            if (mergedOptions.autoHeight) {
                useEventBus('app-auto-height-request').emit()
            }

            if (decodedToken.version < 3) {
                try {
                    await workspace.validateToken(token)
                    logger.trace(`T0ken validated for version < 3`)
                } catch (e: unknown) {
                    const err = e as AxiosError<ValidateTokenResponseError> | undefined
                    const { message, errors } = err?.response?.data || {}

                    const isTokenInvalid = !!message?.toLocaleLowerCase().match(/unauthorized|not authorized/)
                    const isSessionExpired = Array.isArray(errors) &&
                                             errors.some(e => e.code === 'token.notFound')

                    if (isSessionExpired) {
                        useWidgetViewManager().showError({
                            title: t('views.widget.session_expired.title'),
                            description: t('views.widget.session_expired.description'),
                            retry: false,
                            close: true,
                            svgIcon: 'warning.svg',
                        })
                        useEmbed().emitErrorEvent({
                            code: 'fail',
                            message: 'Session expired',
                        })
                    }

                    if (isTokenInvalid) {
                        useWidgetViewManager().showError({
                            title: t('views.widget.invalid_token.title'),
                            description: t('views.widget.invalid_token.description'),
                            retry: false,
                            close: true,
                            svgIcon: 'bug.svg',
                        })
                        useEmbed().emitErrorEvent({
                            code: 'fail',
                            message: 'invalid token',
                        })
                    }

                    return
                }
            }

            initializeWidgetViewManager()

            await setupCustomCss()

            preInitialized.value = true
            logger.trace(`Preinitialized successfully`)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)

            websocketReadyPromise = undefined
            handleInitializationError(e)
        } finally {
            preInitializing.value = false
        }
    }

    const handleInitializationError = (e: unknown) => {
        const { t } = useI18n()
        const { showError } = useWidgetViewManager()
        const { emitErrorEvent } = useEmbed()

        const axiosError = e as AxiosError<{ errors?: Record<'message' | 'code', string>[] } | undefined>

        const errors = axiosError.response?.data?.errors || []

        const isSessionExpiredError = e instanceof Error &&
                                      e.message === SESSION_EXPIRED

        const isWorkflowTerminated = errors.some(e => e.code === 'workflow.terminated')

        if (isSessionExpiredError || isWorkflowTerminated) {
            useWidgetViewManager().showError({
                title: t('views.widget.session_expired.title'),
                description: t('views.widget.session_expired.description'),
                retry: false,
                close: true,
                svgIcon: 'warning.svg',
            })
            emitErrorEvent({
                code: 'fail',
                message: 'Session expired',
            })

            return
        }

        if (e === INITIALIZATION_NO_CONTEXT || axiosError.code === 'ERR_BAD_REQUEST') {
            showError({
                title: t('views.widget.initialization_failed.title'),
                description: t('views.widget.initialization_failed.description'),
                error: e,
                retry: false,
                close: true,
                svgIcon: 'bug.svg',
            })

            emitErrorEvent({
                code: 'fail',
                message: 'Initialization failed',
            })

            return
        }

        const errorMessages = [e, path(['message'], e)]

        if (errorMessages.includes('already preinitialized')) {
            emitErrorEvent({
                code: 'fail',
                message: 'Already preinitialized',
            })
            return
        }

        if (e instanceof WebSocketConnectionError) {
            showError({
                title: t('views.widget.socket_error.title'),
                description: t('views.widget.socket_error.description'),
                retry: true,
                close: true,
                svgIcon: 'bug.svg',
                error: e,
                errorDetails: e,
            })

            emitErrorEvent({
                code: 'fail',
                message: `WebSocket error ${e.code}`,
            })

            return
        }

        showError({
            title: t('views.widget.initialization_failed.title'),
            description: t('views.widget.initialization_failed.description'),
            error: e,
            retry: true,
            close: true,
            svgIcon: 'bug.svg',
        })

        emitErrorEvent({
            code: 'fail',
            message: 'Initialization failed',
        })
    }

    const initialize = async () => {
        const { options } = useWidgetOptions()
        const { t } = useI18n()

        if (options.value.customPagePreview) {
            setWidgetOpened(true)
            initialized.value = true
            return
        }

        logger.trace(`Initialize called`, {
            preInitialized: preInitialized.value,
            preInitializing: preInitializing.value,
        })

        try {
            await until(preInitialized).toBe(true, { timeout: 10 * 1000, throwOnTimeout: true })
        } catch { // eslint-disable-line local-rules/no-error-swallowing
            logger.error('Until preInitialized timeout')

            if (options.value.autoHeight) {
                useEventBus('app-auto-height-request').emit()
            }

            useWidgetViewManager().showError({
                title: t('views.widget.initialization_timeout.title'),
                description: t('views.widget.initialization_timeout.description'),
                close: true,
                svgIcon: 'timeout.svg',
            })

            useEmbed().emitErrorEvent({
                code: 'fail',
                message: 'preinitialization timeout',
            })
        }

        if (options.value.explicitStart) {
            logger.trace(`Start workflow call prevented due to explicitStart=true`)
        } else {
            await startWorkflow()
        }
    }

    const startWorkflow = async () => {
        try {
            logger.trace(`Start workflow called`)

            const { changeLang } = useI18n()
            const { firstMessage } = useWebsocket()
            const { startWidgetManager } = useWidgetViewManager()

            const { token, decodedToken, workflowId, startWorkflow, mode } = useWorkflow()

            if (!token.value) {
                throw new Error('Run transactionlink.setOptions({ token }) first')
            }
            if (initialized.value || initializing.value || widgetOpened.value) {
                logger.warn('already initialized, skipping')
                return
            }

            initializing.value = true

            setWidgetOpened(true)

            if ((decodedToken.value?.version || 2) < 3) {
                const workflow = await useWorkflow().getCurrentWorkflow()

                logger.trace(`Get workflow for t0ken version < 3 (initialize)`)

                if ([
                    'COMPLETED',
                    'FAILED',
                    'TIMEOUT',
                    'TERMINATED',
                    'PAUSED',
                ].includes(workflow?.status || 'no-status')) {
                    throw new Error(SESSION_EXPIRED)
                }
            }

            if (!token.value || !workflowId.value) {
                return
            }

            if (websocketReadyPromise) {
                await websocketReadyPromise
            } else {
                const { initialize: initializeWebsocket } = useWebsocket()
                await initializeWebsocket(token.value, `${workflowId.value}`, mode.value)
            }
            logger.trace(`Websocket initialized`)

            const workflow = await startWorkflow(decodedToken.value?.version)

            const lang = workflow?.locale || decodedToken.value?.lang

            if (lang) {
                changeLang(lang)
                logger.trace(`Language set: ${lang}`)
            }

            logger.trace(`Workflow started`)

            // wait for first message to display first app
            await until(firstMessage)
                .toBeTruthy({ timeout: 600 * 1000, throwOnTimeout: true })

            logger.trace(`First message received`, {
                message: pick(
                    ['partyId', 'taskStatus', 'partyId', 'providerName', 'taskId', 'taskName', 'taskStatus', 'taskType', 'workflowDefinitionId', 'workflowId', 'workflowStatus', 'workspaceId'],
                    firstMessage.value!,
                ),
            })

            if (firstMessage.value) {
                startWidgetManager(firstMessage.value as Message)
            }

            initialized.value = true
            // bypass for reopening widget with defined token
            preInitialized.value = true

            logger.trace(`Initialized successfully`)
        } catch (error) {
            handleInitializationError(error)
        } finally {
            initializing.value = false
        }
    }

    const reinitialize = async () => {
        logger.trace(`Reinitialize called`)

        resetInitialization()

        await promiseTimeout(1000)

        await initialize()

        logger.trace(`Reinitialized successfully`)
    }

    const resetInitialization = () => {
        const { reset: resetWidgetViewManager } = useWidgetViewManager()

        resetWidgetViewManager()
        initialized.value = false
        websocketReadyPromise = undefined
        setWidgetOpened(false)
    }

    const initializeCustomPagePreview = () => {
        const { options, setOptions } = useWidgetOptions()
        const { initialize: initializeWidgetViewManager } = useWidgetViewManager()
        const { setupCustomCssForPreview } = useCustomCss()

        if (!options.value.customPagePreview) {
            setOptions({ customPagePreview: true })
        }
        if (options.value.forcedBreakpoint) {
            document.documentElement.setAttribute('data-breakpoint', options.value.forcedBreakpoint)
        }

        initializeWidgetViewManager()
        setWidgetOpened(true)
        setTimeout(() => {
            setupCustomCssForPreview()
        }, 0)
    }

    const setWidgetOpened = (val: boolean) => {
        widgetOpened.value = val
    }

    return {
        preInitialized,
        preInitialize,
        initialized,
        initialize,
        startWorkflow,
        reinitialize,
        resetInitialization,
        initializeCustomPagePreview,
        widgetOpened: computed(() => widgetOpened.value),
        setWidgetOpened,
    }

}

export default useWidgetHooks
